/**
 * Midas Luxe UI — header panels, mobile drawer, scroll reveals.
 * Vanilla, no dependencies. Dawn's global.js supplies trapFocus.
 */

(() => {
  const MOTION_OK = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FADE_MS = 400;

  /* Honest viewport height on mobile browsers with collapsing chrome */
  const setViewportUnit = () => {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  };
  setViewportUnit();
  window.addEventListener('resize', setViewportUnit, { passive: true });

  class MidasHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.header__bar');
      this.scrim = this.querySelector('.header__scrim');
      this.toggles = [...this.querySelectorAll('.header__nav-link[aria-controls], .header__toggle[aria-controls]')];
      this.openPanel = null;

      this.toggles.forEach((toggle) => {
        if (toggle.matches('a')) return;
        toggle.addEventListener('click', () => {
          const panel = this.querySelector(`#${toggle.getAttribute('aria-controls')}`);
          if (!panel) return;
          panel === this.openPanel ? this.close() : this.open(panel, toggle);
        });
      });

      const cancelClose = () => clearTimeout(this.menuCloseTimer);
      const scheduleClose = () => {
        cancelClose();
        this.menuCloseTimer = setTimeout(() => {
          if (this.openPanel?.classList.contains('header__panel')) this.close();
        }, 180);
      };
      this.querySelectorAll('a.header__nav-link[aria-controls]').forEach((link) => {
        const panel = this.querySelector('#' + link.getAttribute('aria-controls'));
        if (!panel) return;
        const openMenu = () => {
          cancelClose();
          if (this.openPanel !== panel) this.open(panel, link, { focus: false });
        };
        link.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') openMenu();
        });
        link.addEventListener('pointerleave', scheduleClose);
        panel.addEventListener('pointerenter', cancelClose);
        panel.addEventListener('pointerleave', scheduleClose);
        link.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey && this.openPanel === panel)) {
            event.preventDefault();
            openMenu();
            panel.querySelector('a')?.focus();
          }
        });
        panel.addEventListener('keydown', (event) => {
          const links = [...panel.querySelectorAll('a')];
          if (event.key !== 'Tab') return;
          if (event.shiftKey && event.target === links[0]) {
            event.preventDefault();
            this.close();
            link.focus();
          } else if (!event.shiftKey && event.target === links.at(-1)) {
            const next = [...this.bar.querySelectorAll('a, button')].filter((el) => el.getClientRects().length);
            const target = next[next.indexOf(link) + 1];
            if (target) {
              event.preventDefault();
              this.close();
              target.focus();
            }
          }
        });
        [link, panel].forEach((element) => element.addEventListener('focusout', (event) => {
          if (this.openPanel === panel && !panel.contains(event.relatedTarget) && event.relatedTarget !== link) this.close();
        }));
      });

      this.scrim?.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openPanel) {
          const toggle = this.activeToggle;
          this.close();
          toggle?.focus();
        }
      });

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      this.classList.toggle('is-scrolled', window.scrollY > 8);
    }

    show(element) {
      element.hidden = false;
      // Force a frame so the opacity transition has a starting value
      void element.offsetHeight;
      element.classList.add('is-open');
    }

    hide(element) {
      element.classList.remove('is-open');
      if (!MOTION_OK) {
        element.hidden = true;
        return;
      }
      setTimeout(() => {
        if (!element.classList.contains('is-open')) element.hidden = true;
      }, FADE_MS);
    }

    open(panel, toggle, { focus = true } = {}) {
      if (this.openPanel) this.close({ keepState: true });

      this.openPanel = panel;
      this.activeToggle = toggle;
      this.classList.add('is-open');
      this.show(panel);
      if (this.scrim) this.show(this.scrim);

      toggle.setAttribute('aria-expanded', 'true');
      if (toggle.dataset.labelClose) toggle.textContent = toggle.dataset.labelClose;

      const isDrawer = panel.classList.contains('header__drawer');
      if (isDrawer) document.body.classList.add('overflow-hidden');

      const focusTarget = panel.querySelector('input, a, button');
      if (focus && focusTarget) {
        // The panel is mid-fade; wait for it to be paintable before focusing
        this.focusTimer = setTimeout(() => {
          if (this.openPanel === panel) focusTarget.focus({ preventScroll: true });
        }, MOTION_OK ? FADE_MS : 0);
      }
    }

    close({ keepState = false } = {}) {
      clearTimeout(this.menuCloseTimer);
      clearTimeout(this.focusTimer);
      if (!this.openPanel) return;

      this.hide(this.openPanel);
      if (this.scrim && !keepState) this.hide(this.scrim);

      this.toggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        if (toggle.dataset.labelOpen) toggle.textContent = toggle.dataset.labelOpen;
      });

      document.body.classList.remove('overflow-hidden');
      this.openPanel = null;
      if (!keepState) {
        this.classList.remove('is-open');
        this.activeToggle = null;
      }
    }
  }

  customElements.define('midas-header', MidasHeader);

  /* -----------------------------------------------------------------------
     Texture tiles — the title tracks the cursor on fine pointers. On touch,
     and whenever motion is reduced, it stays in the flow under the image so
     it can always be read.
     ----------------------------------------------------------------------- */

  class MidasTiles extends HTMLElement {
    connectedCallback() {
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!fine || !MOTION_OK) return;

      this.classList.add('is-tracking');
      this.frame = null;

      this.addEventListener('pointermove', (event) => {
        const tile = event.target.closest('.tile');
        if (!tile) return;

        // One write per frame — pointermove fires far faster than paint
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const box = tile.getBoundingClientRect();
          const title = tile.querySelector('.tile__title');
          if (!title) return;

          const x = event.clientX - box.left;
          const y = event.clientY - box.top;
          const maxX = box.width - title.offsetWidth;
          const maxY = box.height - title.offsetHeight;

          // Keep the label inside its own tile
          title.style.setProperty('--tile-x', `${Math.max(0, Math.min(x, maxX))}px`);
          title.style.setProperty('--tile-y', `${Math.max(0, Math.min(y, maxY))}px`);
        });
      });
    }

    disconnectedCallback() {
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  }

  customElements.define('midas-tiles', MidasTiles);

  /* -----------------------------------------------------------------------
     Scroll reveals — images settle from 1.05 to 1.0, text fades. Once only.
     ----------------------------------------------------------------------- */

  const revealTargets = () => document.querySelectorAll('.reveal-media:not(.is-revealed), .reveal-fade:not(.is-revealed)');

  if (!MOTION_OK || !('IntersectionObserver' in window)) {
    revealTargets().forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );

  const observeAll = () => revealTargets().forEach((el) => observer.observe(el));
  observeAll();

  // Sections re-render in the theme customiser
  document.addEventListener('shopify:section:load', observeAll);
})();
